import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { HcTableDataSource, SearchBarComponent } from '@healthcatalyst/cashmere';
import { Subscription } from 'rxjs';
import { Filter } from 'src/app/shared/models/filter';
import { AvailableDataColumn, generateStringToSearch } from 'src/app/shared/models/grid';
import { createDateWithUtcOffsetApplied } from 'src/app/shared/utils/date.utils';
import { readFromSessionStorage, writeToSessionStorage } from 'src/app/shared/utils/session-storage.utils';
import { stringSearch } from 'src/app/shared/utils/string.utils';

@Component({
    selector: 'cdp-filter-panel',
    templateUrl: './filter-panel.component.html',
    standalone: false
})
export class FilterPanelComponent implements OnDestroy {
    @Input() filters: Filter[] = [];
    @Input() public dataSource: HcTableDataSource<any>;
    @Input() public searchBar: SearchBarComponent;
    @Input() public dataCols: AvailableDataColumn<any>[];
    @Input() public cacheKey = window.location.pathname;
    @Input() public loading = false;
    @Input() public loadFailed = false;
    @Output() filtersChanged = new EventEmitter<any>();
    @Output() public closeDrawer: EventEmitter<void> = new EventEmitter<void>();
    form: FormGroup = new FormGroup({});
    private valueChangesSub: Subscription;
    private searchBarSub: Subscription;

    init(dataSource: HcTableDataSource<any>, filters: Filter[]) {
        this.dataSource = dataSource;
        this.filters = filters;
        this.buildForm();
        this.subscribeToFormChanges();
        this.dataSource.filterPredicate = this.getFilterPredicate();
        this.searchBarSub?.unsubscribe(); // Clean up old subscription
        this.searchBarSub = this.searchBar?.triggerSearch.subscribe(() => {
            this.applyFilters();
        });
        setTimeout(() => {
            this.applyCachedFilters();
        });
    }

    ngOnDestroy(): void {
        this.valueChangesSub?.unsubscribe();
        this.searchBarSub?.unsubscribe();
    }

    buildForm() {
        const group: { [key: string]: FormControl } = {};
        this.filters.forEach(filter => {
            if (filter.filterType === 'min-max' || filter.filterType === 'date-picker') {
                group[filter.formControlName + 'Min'] = new FormControl(this.getInitialValue(filter));
                group[filter.formControlName + 'Max'] = new FormControl(this.getInitialValue(filter));
            } else {
                group[filter.formControlName] = new FormControl(this.getInitialValue(filter));
            }
        });
        this.form = new FormGroup(group);
    }

    subscribeToFormChanges() {
        if (this.valueChangesSub) {
            this.valueChangesSub.unsubscribe();
        }
        this.valueChangesSub = this.form.valueChanges.subscribe(() => {
            this.filtersChanged.emit(this.form.value);
            this.applyFilters();
        });
    }

    applyCachedFilters() {
        const filterPresets = readFromSessionStorage(this.cacheKey);
        if (filterPresets && typeof filterPresets === 'object') {
            const formValues: { [key: string]: any } = {};
            this.filters.forEach(filter => {
                if (filter.filterType === 'min-max' || filter.filterType === 'date-picker') {
                    if (Object.prototype.hasOwnProperty.call(filterPresets, filter.formControlName + 'Min')) {
                        formValues[filter.formControlName + 'Min'] = filterPresets[filter.formControlName + 'Min'];
                    }
                    if (Object.prototype.hasOwnProperty.call(filterPresets, filter.formControlName + 'Max')) {
                        formValues[filter.formControlName + 'Max'] = filterPresets[filter.formControlName + 'Max'];
                    }
                } else {
                    if (Object.prototype.hasOwnProperty.call(filterPresets, filter.formControlName)) {
                        formValues[filter.formControlName] = filterPresets[filter.formControlName];
                    }
                }
            });
            if (this.searchBar) {
                this.searchBar.setValue?.(filterPresets['searchTerm'] || '');
            }
            this.form.patchValue(formValues);
        }
    }

    applyFilters() {
        if (this.dataSource) {
            const filterObj = { ...this.form.value, searchTerm: this.searchBar?.value };
            this.dataSource.filter = JSON.stringify(filterObj);
            writeToSessionStorage(this.cacheKey, filterObj);
        }
    }

    onSelectionChange(event: any) {
        if (event && event.name) {
            this.form.get(event.name)?.setValue(event.selection);
        }
    }

    resetFilter(filter: Filter) {
        this.form.get(filter.formControlName)?.reset(this.getInitialValue(filter));
    }

    resetAllFilters() {
        const resetValues: { [key: string]: any } = {};
        this.filters.forEach(filter => {
            if (this.hasMinMax(filter)) {
                resetValues[filter.formControlName + 'Min'] = this.getInitialValue(filter);
                resetValues[filter.formControlName + 'Max'] = this.getInitialValue(filter);
            } else {
                resetValues[filter.formControlName] = this.getInitialValue(filter);
            }
        });
        this.form.reset(resetValues);
    }

    private isEmptyFilterValue(val: any): boolean {
        return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
    }

    private hasMinMax(filter: Filter): boolean {
        return filter.filterType === 'min-max' || filter.filterType === 'date-picker';
    }

    getFilterPredicate(): (row: any) => boolean {
        return (row: any) => {
            return (
                this.filters.every(filter => {
                    const value = this.form.get(filter.formControlName)?.value;
                    const hasMinMax = this.hasMinMax(filter);
                    const minValue = hasMinMax ? this.form.get(filter.formControlName + 'Min')?.value : '';
                    const maxValue = hasMinMax ? this.form.get(filter.formControlName + 'Max')?.value : '';

                    // custom filter logic
                    if (filter.customPredicate) {
                        const val = hasMinMax ? { min: minValue, max: maxValue } : value;
                        return filter.customPredicate(val, row);
                    }

                    // default filter logic
                    switch (filter.filterType) {
                        case 'multi-select':
                            if (this.isEmptyFilterValue(value)) return true;
                            return value.some((v: any) => {
                                return (
                                    v[filter.bindLabel || filter.formControlName] ===
                                    row[filter.bindLabel || filter.formControlName]
                                );
                            });
                        case 'yes-no':
                            if (this.isEmptyFilterValue(value)) return true;
                            return value.some((v: { value: boolean }) => row[filter.formControlName] === v.value);
                        case 'min-max':
                            if (!this.isEmptyFilterValue(minValue) && row[filter.formControlName] < minValue) {
                                return false;
                            }
                            if (!this.isEmptyFilterValue(maxValue) && row[filter.formControlName] > maxValue) {
                                return false;
                            }
                            return true;
                        case 'date-picker':
                            if (!this.isEmptyFilterValue(minValue) && row[filter.formControlName]) {
                                const rowDate = new Date(row[filter.formControlName]);
                                const minDate = createDateWithUtcOffsetApplied(new Date(minValue));
                                if (rowDate < minDate) return false;
                            }
                            if (!this.isEmptyFilterValue(maxValue) && row[filter.formControlName]) {
                                const rowDate = new Date(row[filter.formControlName]);
                                const maxDate = createDateWithUtcOffsetApplied(new Date(maxValue));
                                if (rowDate > maxDate) return false;
                            }
                            return true;
                        case 'single-select':
                        default:
                            if (this.isEmptyFilterValue(value)) {
                                return true;
                            }
                            return (row as any)[filter.formControlName] === value;
                    }
                }) && this.applySearchTermFilter(row)
            );
        };
    }

    private applySearchTermFilter(row: any): boolean {
        const stringToSearch = generateStringToSearch(row, this.dataCols);
        return stringSearch(stringToSearch, this.searchBar?.value || '');
    }

    private getInitialValue(filter: Filter): any {
        if (filter.filterType === 'multi-select' || filter.filterType === 'yes-no') {
            return [];
        }
        return '';
    }
}
